import { useEffect, type Dispatch, type SetStateAction } from 'react';
import { defaultOrganizationSettings } from '../fixtures/tenantSettingsFixtures';
import type { TenantSettingsPort } from '../port/TenantSettingsPort';
import { normalizeOrganizationSettings } from './organization';
import type { AsyncResource } from '../../../shared/useAsyncResource';
import type { OrganizationSettings } from './types';
import {
  createLazyResourceStore,
  createWorkspaceStateStore,
  useLazyResource,
  useWorkspaceState,
} from './workspaceStore';
export type TenantSettingsOrganizationWorkspace = {
  resource: AsyncResource<OrganizationSettings>;
  draft: OrganizationSettings;
  baseline: OrganizationSettings;
  setDraft: Dispatch<SetStateAction<OrganizationSettings>>;
  setBaseline: Dispatch<SetStateAction<OrganizationSettings>>;
};
type OrganizationWorkspaceState = Pick<TenantSettingsOrganizationWorkspace, 'draft' | 'baseline'>;
type OrganizationWorkspaceStore = {
  resource: ReturnType<typeof createLazyResourceStore<OrganizationSettings>>;
  state: ReturnType<typeof createWorkspaceStateStore<OrganizationWorkspaceState>>;
  synchronizedData: OrganizationSettings | undefined;
};
const organizationWorkspaceStores = new WeakMap<TenantSettingsPort, OrganizationWorkspaceStore>();
function organizationWorkspaceStore(port: TenantSettingsPort) {
  let store = organizationWorkspaceStores.get(port);
  if (!store) {
    store = {
      resource: createLazyResourceStore<OrganizationSettings>(),
      state: createWorkspaceStateStore<OrganizationWorkspaceState>({
        draft: defaultOrganizationSettings,
        baseline: defaultOrganizationSettings,
      }),
      synchronizedData: undefined,
    };
    organizationWorkspaceStores.set(port, store);
  }
  return store;
}
export function useOrganizationWorkspace(
  port: TenantSettingsPort,
  fixtureMode: boolean,
  onOrganizationChanged: (settings: OrganizationSettings) => void,
): TenantSettingsOrganizationWorkspace {
  const store = organizationWorkspaceStore(port);
  const resource = useLazyResource(
    store.resource,
    () => (fixtureMode ? Promise.resolve(defaultOrganizationSettings) : port.getOrganizationSettings()),
    [fixtureMode, port],
  );
  const { draft, baseline } = useWorkspaceState(store.state);
  useEffect(() => {
    if (!resource.data || store.synchronizedData === resource.data) return;
    const normalized = normalizeOrganizationSettings(resource.data);
    store.synchronizedData = resource.data;
    store.state.update(() => ({
      draft: normalized,
      baseline: normalized,
    }));
  }, [resource.data, store]);
  useEffect(() => {
    if (resource.data) onOrganizationChanged(normalizeOrganizationSettings(resource.data));
  }, [onOrganizationChanged, resource.data]);
  const setDraft: Dispatch<SetStateAction<OrganizationSettings>> = (update) =>
    store.state.update((current) => ({
      ...current,
      draft: typeof update === 'function' ? update(current.draft) : update,
    }));
  const setBaseline: Dispatch<SetStateAction<OrganizationSettings>> = (update) =>
    store.state.update((current) => ({
      ...current,
      baseline: typeof update === 'function' ? update(current.baseline) : update,
    }));
  return {
    resource,
    draft,
    baseline,
    setDraft,
    setBaseline,
  };
}
